import dayjs from 'dayjs';
import { User } from '/@/modules/user/models/User';
import { VaultDistributionSetting } from '/@/modules/vaultdistributionsetting/models/VaultDistributionSetting';
import { EstateDistribution } from '/@/modules/estatedistribution/models/EstateDistribution';
import { Asset } from '/@/modules/asset/models/Asset';
import { GuidanceStep } from '/@/modules/guidancestep/models/GuidanceStep';
import { Contact } from '/@/modules/contact/models/Contact';
import { Estate } from '/@/modules/estate/models/Estate';
import { ContactMarriageHistory } from '/@/modules/contact/models/ContactMarriageHistory';
import { MinorDistributionInheritanceAgeType } from '/@/modules/vaultdistributionsetting/constants/MinorDistributionInheritanceAgeType';
import { VaultDistributionRecipientType } from '/@/modules/vaultdistributionsetting/enums/VaultDistributionRecipientType';
import { StepKey } from '/@/modules/guidancestep/enums/StepKey';
import { DateFormat } from '/@/modules/core/app/constants/date.constants';
import { Will } from '/@/modules/will/models/Will';
import { BaseModel } from '/@/modules/core/app/models/abstracts/BaseModel';
import { Pet } from '@/modules/pet/models/Pet';
import { FuneralPreArrangementServiceType } from '@/modules/asset/constants/FuneralPreArrangementServiceType';
import { WillSectionKey } from '@/modules/will/constants/WillSectionKey';

export class PlanningProductStageCompletionDetail extends BaseModel {
  estate: Estate | null;
  planner: User | null;
  hasChildren: boolean;
  children: User[] | null;
  spouse: User | null;
  commonLawPartner: User | null;
  pets: Pet[] | null;
  petTutor: User | null;
  alternatePetTutor: User | null;
  lumpSumGiftAmount?: number;
  primaryExecutors: User[] | null;
  substituteExecutors: User[] | null;
  vaultDistributionSetting: VaultDistributionSetting | null;
  specificBeneficiaries: User[] | null;
  mainBeneficiaries: User[] | null;
  mainBeneficiariesWithAlternate: User[] | null;
  funeralPreArrangementFuneralHome: Asset | null;
  funeralPreArrangementCemeteryProperty: Asset | null;
  guidanceSteps: GuidanceStep[];
  contactMarriageHistory: ContactMarriageHistory | null;
  will: Will | null;
  hasFuneralArrangementV2Feature: boolean;

  constructor(model: Partial<PlanningProductStageCompletionDetail> = {}) {
    super(model);

    this.hasChildren = model.hasChildren ?? false;

    this.estate = Estate.mapModel(model.estate);
    this.planner = User.mapModelOrNull(model.planner);
    this.children = User.mapModelsOrNull(model.children);
    this.spouse = User.mapModelOrNull(model.spouse);
    this.commonLawPartner = User.mapModelOrNull(model.commonLawPartner);
    this.primaryExecutors = User.mapModelsOrNull(model.primaryExecutors);
    this.substituteExecutors = User.mapModelsOrNull(model.substituteExecutors);
    this.vaultDistributionSetting = VaultDistributionSetting.mapModelOrNull(
      model.vaultDistributionSetting
    );
    this.specificBeneficiaries = User.mapModelsOrNull(model.specificBeneficiaries);
    this.mainBeneficiaries = User.mapModelsOrNull(model.mainBeneficiaries);
    this.mainBeneficiariesWithAlternate = User.mapModelsOrNull(
      model.mainBeneficiariesWithAlternate
    );
    this.funeralPreArrangementFuneralHome = Asset.mapModelOrNull(
      model.funeralPreArrangementFuneralHome
    );
    this.funeralPreArrangementCemeteryProperty = Asset.mapModelOrNull(
      model.funeralPreArrangementCemeteryProperty
    );
    this.guidanceSteps = GuidanceStep.mapModels(model.guidanceSteps);
    this.contactMarriageHistory = ContactMarriageHistory.mapModelOrNull(
      model.contactMarriageHistory
    );
    this.will = Will.mapModelOrNull(model.will);
    this.pets = Pet.mapModelsOrNull(model.pets);
    this.petTutor = User.mapModelOrNull(model.petTutor);
    this.alternatePetTutor = User.mapModelOrNull(model.alternatePetTutor);
    this.lumpSumGiftAmount = model.lumpSumGiftAmount;
    this.hasFuneralArrangementV2Feature = model.hasFuneralArrangementV2Feature ?? false;
  }

  get minorChildren(): User[] | null {
    return this.children?.filter((child: User) => child.contact.isAMinor) ?? null;
  }

  getStep(key: number): GuidanceStep | undefined {
    return this.guidanceSteps.find((step: GuidanceStep) => step.key == key);
  }

  get hasSpouse(): boolean {
    return this.planner?.contact.isMarried;
  }

  get hasPartner(): boolean {
    return this.planner?.contact.hasCommonLawPartner;
  }

  get isPersonalInformationComplete(): boolean {
    const plannerContact: Contact | undefined = this.planner?.contact;

    const plannerContactCompleted: boolean =
      !!plannerContact?.firstName &&
      !!plannerContact?.lastName &&
      !!plannerContact?.dateOfBirth &&
      !!plannerContact?.maritalStatus;

    if (!plannerContactCompleted) {
      return false;
    }

    const plannerAddressCompleted: boolean =
      !!plannerContact?.address?.countryId && !!plannerContact?.address?.city;

    let marriageCompleted: boolean = true;
    if (
      this.estate?.isInQuebecJurisdiction &&
      plannerContact?.isMarried &&
      !!this.contactMarriageHistory
    ) {
      marriageCompleted = this.contactMarriageHistory.isCompleted();
    }

    return plannerAddressCompleted && marriageCompleted;
  }

  get isSpouseInformationComplete(): boolean {
    const spouseContact: Contact | undefined = this.spouse?.contact;

    if (!spouseContact) {
      return false;
    }

    return (
      !!spouseContact.firstName &&
      !!spouseContact.lastName &&
      !!this.spouse?.estateAccount?.meta?.relationshipType
    );
  }

  get isCommonLawPartnerInformationComplete(): boolean {
    const commonLawPartnerContact: Contact | undefined = this.commonLawPartner?.contact;

    if (!commonLawPartnerContact) {
      return false;
    }

    return (
      !!commonLawPartnerContact.firstName &&
      !!commonLawPartnerContact.lastName &&
      !!this.commonLawPartner?.estateAccount?.meta?.relationshipType
    );
  }

  get isChildrenInformationComplete(): boolean {
    return !this.children?.some((child: User) => {
      return child.contact.isAMinor
        ? !this.isMinorChildComplete(child)
        : !this.isAdultChildCompete(child);
    });
  }

  isMinorChildComplete(child: User): boolean {
    const childContact: Contact | undefined = child?.contact;
    const childInformationComplete: boolean =
      !!childContact?.firstName && !!childContact.lastName && !!childContact.dateOfBirth;

    if (!childInformationComplete) {
      return false;
    }

    let careGiverComplete: boolean = true;
    let guardianComplete: boolean = true;
    let alternateGuardianComplete: boolean = true;

    if (child.estateAccount?.meta?.hasCareGiver) {
      const careGiver: User = child.estateAccount.careGiverUsers[0];
      careGiverComplete = careGiver ? this.hasFullName(careGiver) : false;
    }

    if (child.estateAccount.guardianUsers?.length) {
      guardianComplete = this.hasFullName(child.estateAccount.guardianUsers[0]) ?? false;

      if (child.estateAccount.alternateGuardianUsers?.length) {
        alternateGuardianComplete =
          this.hasFullName(child.estateAccount.alternateGuardianUsers[0]) ?? false;
      }
    }

    return careGiverComplete && guardianComplete && alternateGuardianComplete;
  }

  isAdultChildCompete(child: User): boolean {
    const childContact: Contact | undefined = child?.contact;

    return !!childContact.firstName && !!childContact.lastName && !!childContact.dateOfBirth;
  }

  get isExecutorsInformationComplete(): boolean {
    if (!this.primaryExecutors || this.primaryExecutors.length === 0) {
      return false;
    }

    return !!this.primaryExecutors.every(
      (executor: User) =>
        this.hasFullName(executor) && executor.estateAccount?.meta?.relationshipType
    );
  }

  get isAlternateExecutorsInformationComplete(): boolean {
    if (
      this.primaryExecutors?.some((executor: User) => executor.estateAccount?.isExecutorProfessional)
    ) {
      return true;
    }

    if (!this.substituteExecutors || this.substituteExecutors.length === 0) {
      return false;
    }

    return !!this.substituteExecutors.every(
      (executor: User) =>
        this.hasFullName(executor) && executor.estateAccount?.meta?.relationshipType
    );
  }

  get isMinorChildrenGuardianInformationComplete(): boolean {
    return !this.minorChildren?.some((child: User) => {
      return (
        !child.estateAccount?.guardianUsers?.length ||
        !child.estateAccount?.alternateGuardianUsers?.length
      );
    });
  }

  get isSpecificBeneficiariesInformationComplete(): boolean {
    return !this.specificBeneficiaries?.some(
      (specificBeneficiary: User) =>
        !this.hasFullName(specificBeneficiary) ||
        !this.isSpecificDistributionComplete(specificBeneficiary)
    );
  }

  isSpecificDistributionComplete(specificBeneficiary: User): boolean {
    const specificDistributions: EstateDistribution[] | undefined =
      specificBeneficiary.estateAccount.specificDistributions;

    if (!specificDistributions?.length) {
      return false;
    }

    return !specificDistributions.some(
      (distribution: EstateDistribution) =>
        (distribution.assetId && (!distribution.asset?.name || !distribution.sharePercentage)) ||
        (!distribution.assetId && !distribution?.plannedAmount)
    );
  }

  get isResidualBeneficiariesComplete(): boolean {
    if (!this.vaultDistributionSetting?.recipientType) {
      return false;
    }

    if (this.vaultDistributionSetting?.recipientType !== VaultDistributionRecipientType.OTHER.key) {
      return true;
    }

    return !this.mainBeneficiaries?.some(
      (mainBeneficiary: User) =>
        !this.hasFullName(mainBeneficiary) || !this.isValidMainBeneficiary(mainBeneficiary)
    );
  }

  get isAlternateBeneficiariesComplete(): boolean {
    if (
      !this.vaultDistributionSetting?.spouseAlternateRecipientType &&
      !this.vaultDistributionSetting?.childrenAlternateRecipientType &&
      !this.vaultDistributionSetting?.otherBeneficiaryAlternateRecipientType
    ) {
      return (
        !!this.mainBeneficiaries?.length &&
        this.mainBeneficiaries.every(
          (beneficiary: User) => beneficiary.estateAccount?.isOrganizationBeneficiary
        )
      );
    }

    return !this.mainBeneficiariesWithAlternate?.some(
      (mainBeneficiary: User) =>
        !this.hasFullName(mainBeneficiary) || !this.isValidAlternateBeneficiary(mainBeneficiary)
    );
  }

  isValidMainBeneficiary(mainBeneficiary: User): boolean {
    const distributions: EstateDistribution[] | undefined =
      mainBeneficiary.estateAccount.residualDistributions;

    if (!distributions?.length) {
      return false;
    }

    return !!distributions[0].sharePercentage || !!distributions[0].isSplitEqually;
  }

  isValidAlternateBeneficiary(mainBeneficiary: User): boolean {
    if (mainBeneficiary.estateAccount?.isOrganizationBeneficiary) {
      return true;
    }

    if (!mainBeneficiary.estateAccount?.alternateBeneficiaries?.length) {
      return false;
    }

    return !mainBeneficiary.estateAccount?.alternateBeneficiaries.find(
      (user: User) => !this.hasFullName(user) && !this.isValidOrganization(user)
    );
  }

  get isStagedDistributionComplete(): boolean {
    if (!this.vaultDistributionSetting?.minorDistributionAgeType) {
      return false;
    }

    if (
      this.vaultDistributionSetting?.minorDistributionAgeType ===
      MinorDistributionInheritanceAgeType.MAJORITY.key
    ) {
      return true;
    }

    if (
      this.vaultDistributionSetting?.minorDistributionAgeType ===
      MinorDistributionInheritanceAgeType.SPECIFIC_AGE.key
    ) {
      return !!this.vaultDistributionSetting?.minorDistributionAge;
    }

    if (
      this.vaultDistributionSetting?.minorDistributionAgeType ===
      MinorDistributionInheritanceAgeType.MULTIPLE_SPECIFIC_AGES.key
    ) {
      return !!this.vaultDistributionSetting?.estateDistributionInheritanceAges?.length;
    }

    return true;
  }

  get isLastWishesCompleted(): boolean {
    if (this.hasLastWishPreArrangement) {
      return !!this.funeralPreArrangementFuneralHome.meta.serviceProviderName;
    }

    if (!this.hasLastWishPreArrangement) {
      if (!this.funeralPreArrangementFuneralHome) {
        return false;
      }

      if (this.funeralPreArrangementFuneralHome.meta?.isFinalDispositionTypeLetExecutorDecide) {
        return true;
      }

      return (
        !!this.funeralPreArrangementFuneralHome.meta.finalDispositionType &&
        !!this.funeralPreArrangementFuneralHome.meta.serviceType &&
        (this.funeralPreArrangementFuneralHome.meta.serviceType ===
          FuneralPreArrangementServiceType.NO_SERVICE.key ||
          !!this.funeralPreArrangementFuneralHome.meta.ceremonyType)
      );
    }

    return false;
  }

  get hasLastWishPreArrangement(): boolean {
    if (this.hasFuneralArrangementV2Feature) {
      const step: GuidanceStep | undefined = this.getStep(StepKey.STEP_922045);

      return step?.meta?.hasFuneralPreArrangement;
    }

    const step: GuidanceStep | undefined = this.getStep(
      this.estate?.isClearWill ? StepKey.STEP_340074 : StepKey.STEP_749361
    );

    return step?.meta?.hasPreArrangement;
  }

  get hasPetGuardianshipCompleted(): boolean {
    // @todo olivier pet step CV
    const step: GuidanceStep | undefined = this.getStep(
      this.estate?.isClearWill ? StepKey.STEP_498673 : StepKey.STEP_525888
    );

    if (step?.meta?.hasPets === false) {
      return true;
    }

    if (step?.meta?.hasPets === true) {
      return !!this.pets?.length && !!this.petTutor;
    }

    return false;
  }

  get isCemeteryPropertyCompleted(): boolean {
    if (this.hasFuneralArrangementV2Feature) {
      const step: GuidanceStep | undefined = this.getStep(StepKey.STEP_922045);

      if (step?.meta?.hasCemeteryProperty === false) {
        return true;
      }

      if (step?.meta?.hasCemeteryProperty !== true) {
        return false;
      }

      if (!this.funeralPreArrangementCemeteryProperty) {
        return false;
      }

      return (
        !!this.funeralPreArrangementCemeteryProperty.meta.serviceProviderName &&
        !!this.funeralPreArrangementCemeteryProperty.address?.fullAddress
      );
    }

    const step: GuidanceStep | undefined = this.getStep(
      this.estate?.isClearWill ? StepKey.STEP_146420 : StepKey.STEP_919460
    );

    if (step?.meta.hasPreArrangement === false) {
      return true;
    }

    if (step?.meta.hasPreArrangement !== true) {
      return false;
    }

    if (!this.funeralPreArrangementCemeteryProperty) {
      return false;
    }

    return (
      !!this.funeralPreArrangementCemeteryProperty.meta.serviceProviderName &&
      !!this.funeralPreArrangementCemeteryProperty.address?.fullAddress
    );
  }

  get hasCemeteryPreArrangement(): boolean {
    if (this.hasFuneralArrangementV2Feature) {
      const step: GuidanceStep | undefined = this.getStep(StepKey.STEP_922045);

      return step?.meta?.hasCemeteryProperty;
    }

    const step: GuidanceStep | undefined = this.getStep(
      this.estate?.isClearWill ? StepKey.STEP_146420 : StepKey.STEP_919460
    );

    return step?.meta.hasPreArrangement;
  }

  get isStagedDistributionInvalid(): boolean {
    return (
      this.estate?.isInQuebecJurisdiction ||
      this.stepIsLocked(this.estate?.isClearWill ? StepKey.STEP_327133 : StepKey.STEP_951035)
    );
  }

  get isExtendedAdministrationInvalid(): boolean {
    return (
      !this.estate?.isInQuebecJurisdiction ||
      this.stepIsLocked(this.estate?.isClearWill ? StepKey.STEP_994882 : StepKey.STEP_355881)
    );
  }

  hasFullName(user: User): boolean {
    if (user.estateAccount?.isOrganization) {
      return !!user.contact?.fullName;
    }

    return !!user.contact?.firstName && !!user.contact.lastName;
  }

  isValidOrganization(user: User): boolean {
    return (
      !!user.estateAccount?.meta?.organizationName && !!user.estateAccount?.meta?.registrationNumber
    );
  }

  get plannerDateOfBirth(): string {
    return dayjs(this.planner?.contact.dateOfBirth).format(DateFormat.FULL_MONTH_DAY_YEAR);
  }

  stepIsLocked(stepKey: number): boolean {
    const step: GuidanceStep | undefined = this.getStep(stepKey);

    return step?.isLocked;
  }

  get hasAllWillInformationCollected(): boolean {
    if (!this.isSpouseInformationComplete && this.hasSpouse) {
      return false;
    }

    if (!this.isCommonLawPartnerInformationComplete && this.hasPartner) {
      return false;
    }

    if (!this.isChildrenInformationComplete && this.hasChildren) {
      return false;
    }

    if (
      !(this.estate?.isInQuebecJurisdiction && this.estate.isSelfServePlan) &&
      (!this.isStagedDistributionInvalid || !this.isExtendedAdministrationInvalid) &&
      !this.isStagedDistributionComplete
    ) {
      return false;
    }

    return (
      this.isPersonalInformationComplete &&
      this.isExecutorsInformationComplete &&
      this.isAlternateExecutorsInformationComplete &&
      this.isMinorChildrenGuardianInformationComplete &&
      this.isLastWishesCompleted &&
      this.isCemeteryPropertyCompleted &&
      this.isResidualBeneficiariesComplete &&
      this.isAlternateBeneficiariesComplete &&
      this.isSpecificBeneficiariesInformationComplete
    );
  }

  get incompleteWillSectionNames(): WillSectionKey[] {
    const sections: WillSectionKey[] = [];

    if (!this.isPersonalInformationComplete) {
      sections.push(WillSectionKey.PERSONAL_INFORMATION);
    }

    if (this.hasSpouse && !this.isSpouseInformationComplete) {
      sections.push(WillSectionKey.SPOUSE_INFORMATION);
    }

    if (this.hasPartner && !this.isCommonLawPartnerInformationComplete) {
      sections.push(WillSectionKey.PARTNER_INFORMATION);
    }

    if (this.hasChildren && !this.isChildrenInformationComplete) {
      sections.push(WillSectionKey.CHILDREN_INFORMATION);
    }

    if (!this.isExecutorsInformationComplete) {
      sections.push(WillSectionKey.EXECUTOR_INFORMATION);
    }

    if (!this.isAlternateExecutorsInformationComplete) {
      sections.push(WillSectionKey.ALTERNATE_EXECUTOR_INFORMATION);
    }

    if (!this.isMinorChildrenGuardianInformationComplete) {
      sections.push(WillSectionKey.MINOR_CHILDREN_GUARDIANS);
    }

    if (!this.isSpecificBeneficiariesInformationComplete) {
      sections.push(WillSectionKey.SPECIFIC_BENEFICIARIES);
    }

    if (!this.isResidualBeneficiariesComplete || !this.isAlternateBeneficiariesComplete) {
      sections.push(WillSectionKey.RESIDUAL_BENEFICIARIES);
    }

    if (
      !(this.estate?.isInQuebecJurisdiction && this.estate.isSelfServePlan) &&
      (!this.isStagedDistributionInvalid || !this.isExtendedAdministrationInvalid) &&
      !this.isStagedDistributionComplete
    ) {
      sections.push(WillSectionKey.STAGED_DISTRIBUTION);
    }

    if (this.hasFuneralArrangementV2Feature) {
      if (!this.isLastWishesCompleted || !this.isCemeteryPropertyCompleted) {
        sections.push(WillSectionKey.LAST_WISHES);
      }
    } else {
      if (!this.isLastWishesCompleted) {
        sections.push(WillSectionKey.LAST_WISHES);
      }

      if (!this.isCemeteryPropertyCompleted) {
        sections.push(WillSectionKey.CEMETERY_PROPERTY);
      }
    }

    return sections;
  }
}
